/**
 * Team Avatars Component
 * Displays owner and assisting team members with truncation and tooltip
 */

'use client';

import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

// Helper functions
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// All avatars use the same neutral color scheme
export function getAvatarColor(_name: string, _index: number): string {
  return 'bg-muted';
}

interface TeamAvatarsProps {
  assignedSolicitor?: string;
  assistingTeamMembers?: string[];
  /**
   * Tooltip heading for the second group. Defaults to the matter vocabulary
   * every shipped consumer relies on.
   *
   * It exists so a surface whose second group is a *role* rather than a team can
   * name it honestly instead of forking a copy of this component.
   */
  assistingLabel?: string;
  /**
   * Tooltip heading for the **first** person — the sibling of
   * {@link assistingLabel}, and load-bearing on exactly one surface.
   *
   * `Owner` is the matter vocabulary and is right for every matter consumer. On
   * an **enquiry** it is a regression: *owner* already means the matter owner,
   * and a converted lead puts both on screen two clicks apart, so an unqualified
   * `Owner` on a lead surface claims something the enquiry does not have (see
   * `backend/src/leads/CLAUDE.md`). The leads board passes `Solicitor`.
   */
  ownerLabel?: string;
}

export function TeamAvatars({
  assignedSolicitor,
  assistingTeamMembers,
  assistingLabel = 'Assisting Team Members',
  ownerLabel = 'Owner'
}: TeamAvatarsProps) {
  if (!assignedSolicitor && !assistingTeamMembers) return null;

  const allMembers = [
    ...(assignedSolicitor ? [assignedSolicitor] : []),
    ...(assistingTeamMembers || [])
  ];

  const totalMembers = allMembers.length;
  const visibleMembers = allMembers.slice(0, 3);
  const remainingCount = totalMembers - 3;

  return (
    <TooltipProvider>
      <Tooltip>
        {visibleMembers.length ? (
          <TooltipTrigger asChild>
            <div className="ms-2 flex items-center gap-1">
              {visibleMembers.map((member, index) => (
                <div
                  // eslint-disable-next-line @eslint-react/no-array-index-key -- member names may duplicate; index disambiguates positionally
                  key={`${member}-${index}`}
                  className={cn(
                    'text-12px text-muted-foreground flex h-6 w-6 items-center justify-center rounded-full font-semibold',
                    getAvatarColor(member, index)
                  )}
                >
                  {getInitials(member)}
                </div>
              ))}
              {remainingCount > 0 && (
                <div className="text-12px bg-muted text-muted-foreground flex h-6 w-6 items-center justify-center rounded-full font-semibold">
                  +{remainingCount}
                </div>
              )}
            </div>
          </TooltipTrigger>
        ) : null}

        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-2">
            {assignedSolicitor && (
              <div>
                <p className="text-xs font-semibold">{ownerLabel}</p>
                <p className="text-xs">{assignedSolicitor}</p>
              </div>
            )}
            {assistingTeamMembers && assistingTeamMembers.length > 0 && (
              <div>
                <p className="text-xs font-semibold">{assistingLabel}</p>
                <ul className="space-y-0.5 text-xs">
                  {assistingTeamMembers.map((member) => (
                    <li key={member}>{member}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
